import React, { useState } from "react";
import { FiActivity, FiThermometer, FiDroplet, FiZap } from "react-icons/fi";
import { HealthRing, MiniBar, StatusBadge, healthColor, riskColor } from "./fleetHelpers";

const Gauge = ({ label, value, max, unit, color, icon: Icon }) => {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="tl-gauge">
      <div className="tl-gauge-header">
        {Icon && <Icon size={14} color={color} />}
        <span className="tl-gauge-label">{label}</span>
      </div>
      <div className="tl-gauge-val" style={{ color }}>{value}<span className="tl-gauge-unit"> {unit}</span></div>
      <MiniBar value={pct} max={100} color={color} height={8} />
      <div className="tl-gauge-range">0 – {max} {unit}</div>
    </div>
  );
};

const TelemetryPanel = ({ d }) => {
  const [selectedId, setSelectedId] = useState(d.vehicles[0]?.id);
  const v = d.vehicles.find(veh => veh.id === selectedId) || d.vehicles[0];

  return (
    <div className="fleet-telemetry">

      {/* ── Vehicle Selector ── */}
      <div className="tl-selector-bar">
        <label className="tl-select-label">Select Vehicle</label>
        <select
          className="tl-select"
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
        >
          {d.vehicles.map(veh => (
            <option key={veh.id} value={veh.id}>
              {veh.name} ({veh.id}) – {veh.status}
            </option>
          ))}
        </select>
      </div>

      <div className="tl-content">

        {/* ── Left: Vehicle Info ── */}
        <div className="tl-info-card">
          <div className="tl-vehicle-header">
            <div>
              <div className="tl-veh-name">{v.name}</div>
              <div className="tl-veh-sub">{v.plateNo} · {v.type}</div>
              <div className="tl-veh-driver">Driver: {v.driver}</div>
            </div>
            <StatusBadge status={v.status} />
          </div>

          <div className="tl-health-row">
            <div className="tl-health-ring-wrap">
              <HealthRing value={v.healthScore} size={90} stroke={9} />
              <div className="tl-health-center">
                <span style={{ fontSize: 18, fontWeight: 700, color: healthColor(v.healthScore) }}>{v.healthScore}</span>
                <span style={{ fontSize: 10, color: "#64748b" }}>Health</span>
              </div>
            </div>
            <div className="tl-health-meta">
              <div className="tl-hm-row">
                <span className="tl-hm-label">Failure Risk</span>
                <span className="tl-hm-val" style={{ color: riskColor(v.failureRisk) }}>{v.failureRisk}%</span>
              </div>
              <MiniBar value={v.failureRisk} color={riskColor(v.failureRisk)} height={6} />
              <div className="tl-hm-row" style={{ marginTop: 8 }}>
                <span className="tl-hm-label">Last Service</span>
                <span className="tl-hm-val">{v.lastService}</span>
              </div>
              <div className="tl-hm-row">
                <span className="tl-hm-label">Next Service</span>
                <span className="tl-hm-val">{v.nextService}</span>
              </div>
            </div>
          </div>

          <div className="tl-info-grid">
            <div className="tl-info-item">
              <span className="tl-info-lbl">Odometer</span>
              <span className="tl-info-val">{v.odometer.toLocaleString()} km</span>
            </div>
            <div className="tl-info-item">
              <span className="tl-info-lbl">Idle Time</span>
              <span className="tl-info-val">{v.idleTime} min</span>
            </div>
            <div className="tl-info-item">
              <span className="tl-info-lbl">Brake Usage</span>
              <span className="tl-info-val">{v.brakeUsage}</span>
            </div>
            <div className="tl-info-item">
              <span className="tl-info-lbl">Route</span>
              <span className="tl-info-val">{v.route}</span>
            </div>
            <div className="tl-info-item">
              <span className="tl-info-lbl">ETA</span>
              <span className="tl-info-val">{v.eta}</span>
            </div>
            <div className="tl-info-item">
              <span className="tl-info-lbl">Location</span>
              <span className="tl-info-val">{v.location}</span>
            </div>
          </div>

          {v.alerts.length > 0 && (
            <div className="tl-alerts">
              <div className="tl-alerts-title">Active Alerts</div>
              {v.alerts.map((a, i) => (
                <div key={i} className="tl-alert-row">⚠ {a}</div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: Sensor Gauges ── */}
        <div className="tl-gauges">
          <div className="tl-gauges-title">Live Telemetry</div>
          <div className="tl-gauges-grid">
            <Gauge label="Speed"          value={v.speed}                     max={120}  unit="km/h" color="#667eea" icon={FiActivity}     />
            <Gauge label="RPM"            value={v.rpm}                       max={4000} unit="RPM"  color="#3b82f6" icon={FiActivity}     />
            <Gauge label="Engine Temp"    value={v.engineTemp}                max={120}  unit="°C"   color={v.engineTemp > 95 ? "#ef4444" : "#f59e0b"} icon={FiThermometer} />
            {v.isEV ? (
              <Gauge label="Battery"       value={v.battery}                   max={100}  unit="%"    color="#06b6d4" icon={FiZap}          />
            ) : (
              <Gauge label="Battery"       value={v.battery}                   max={15}   unit="V"    color="#10b981" icon={FiZap}          />
            )}
            {!v.isEV && (
              <Gauge label="Fuel Level"    value={v.fuel}                      max={100}  unit="%"    color={v.fuel < 30 ? "#ef4444" : "#10b981"} icon={FiDroplet} />
            )}
            {v.isEV && (
              <Gauge label="Range"         value={v.range}                     max={300}  unit="km"   color="#06b6d4" icon={FiActivity}     />
            )}
          </div>

          {/* EV-specific info */}
          {v.isEV && (
            <div className="tl-ev-card">
              <div className="tl-ev-title">⚡ Electric Vehicle Info</div>
              <div className="tl-ev-grid">
                <div className="tl-ev-item">
                  <span>Charging Cycles</span>
                  <strong>{v.chargingCycles}</strong>
                </div>
                <div className="tl-ev-item">
                  <span>Remaining Range</span>
                  <strong>{v.range} km</strong>
                </div>
                <div className="tl-ev-item">
                  <span>Battery Level</span>
                  <strong>{v.battery}%</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TelemetryPanel;
